import { ShopOptionsSchema } from "./schema";
export function productCopy(product) {
    return { details: product.details, availability: product.availability, variants: product.variants.map(({ rowVersion: _version, ...variant }) => variant) };
}
/** Identity uses stable option/value ids, independent of labels and display order. */
export function variantKey(values) { return JSON.stringify(Object.entries(values).sort(([a], [b]) => a.localeCompare(b))); }
/** Reuses variant ids for existing combinations. The empty Cartesian product is one hidden default variant. */
export function variantsFromOptions(options, previous, currency, newId, price = null) {
    const valid = ShopOptionsSchema.parse(options);
    const combinations = valid.reduce((rows, option) => rows.flatMap((row) => option.values.map((value) => ({ ...row, [option.id]: value.id }))), [{}]);
    const known = new Map(previous.map((variant) => [variantKey(variant.values), variant]));
    return combinations.map((values) => known.get(variantKey(values)) ?? { id: newId(), values, price, compareAt: null, currency, sku: "", photo: null, available: true, files: null });
}
/** Membership includes descendants once each, and preserves the catalogue's order. */
export function categoryProductIds(state, categoryId, includeHidden = false) {
    const ids = new Set([categoryId]);
    let size = 0;
    while (size !== ids.size) {
        size = ids.size;
        for (const category of state.categories)
            if (category.parentId && ids.has(category.parentId))
                ids.add(category.id);
    }
    return state.products.filter((product) => (includeHidden || product.availability === "available") && product.details.categories.some((id) => ids.has(id))).map((product) => product.id);
}
export function productStatus(product, stock) {
    if (product.availability === "hidden")
        return "hidden";
    return product.variants.some((variant) => {
        const inventory = stock.find((row) => row.variantId === variant.id);
        return variant.available && inventory !== undefined && (!inventory.tracked || inventory.quantity > 0);
    }) ? "available" : "sold-out";
}
export const SHOP_MENU_LINK_ID = "shop-menu-link";
/** Module state is laid over Website only in memory. Never saved into a Website version. */
export function stripShop(content) {
    const next = structuredClone(content);
    for (const [id, record] of Object.entries(next.recordsById))
        if (record.kind === "product" || id === SHOP_MENU_LINK_ID)
            delete next.recordsById[id];
    for (const [id, collection] of Object.entries(next.collections))
        if (collection.rule?.kind === "product")
            delete next.collections[id];
    if (next.collections.menu?.memberIds)
        next.collections.menu.memberIds = next.collections.menu.memberIds.filter((id) => id !== SHOP_MENU_LINK_ID);
    if (next.modules)
        delete next.modules.shop;
    return next;
}
export function withShop(content, state, mode) {
    const next = stripShop(content);
    if (!state)
        return next;
    for (const product of state.products) {
        if (mode === "live" && product.availability !== "available")
            continue;
        const d = product.details;
        const variants = product.variants.map((v) => { const stock = state.stock.find((s) => s.variantId === v.id); return { id: v.id, values: v.values, price: v.price, compareAt: v.compareAt, sku: v.sku, photo: v.photo, available: Boolean(v.price !== null && v.available && stock && (!stock.tracked || stock.quantity > 0)) }; });
        const prices = variants.flatMap((v) => v.price === null ? [] : [v.price]);
        next.recordsById[product.id] = { id: product.id, kind: "product", fields: { name: d.name, slug: d.slug, revision: d.revision, categories: d.categories, createdAt: product.createdAt, status: productStatus(product, state.stock), currency: state.settings.currency, minPrice: prices.length ? Math.min(...prices) : null, maxPrice: prices.length ? Math.max(...prices) : null, photos: d.photos, options: d.options, variants, physical: d.physical, downloads: d.downloads, customizations: state.customizationSets.filter((set) => d.customizationSetIds.includes(set.id)), textFields: d.textFields } };
    }
    next.collections.products = { rule: { kind: "product", sort: "oldest" } };
    for (const c of state.categories)
        next.collections[c.id] = { rule: { kind: "product", tag: c.id, sort: "oldest" }, group: { label: c.name, slug: c.slug, ...(c.parentId ? { parentId: c.parentId } : {}), description: c.description, showInMenu: c.showInMenu } };
    next.modules = { ...next.modules, shop: { name: state.settings.name, base: state.settings.base, enabled: state.enabled !== false, addToMenu: state.enabled !== false, currency: state.settings.currency } };
    const destinations = new Set([...state.products.filter((p) => mode === "working" || p.availability === "available").map((p) => `${state.settings.base}/${p.details.slug}`), ...state.categories.map((c) => `${state.settings.base}/c/${c.slug}`), state.settings.base]);
    const redirects = (state.redirects ?? []).filter((r) => destinations.has(r.to));
    if (redirects.length)
        next.redirects = [...(next.redirects ?? []).filter((r) => !redirects.some((own) => own.from === r.from)), ...redirects];
    if (state.enabled !== false)
        next.recordsById[SHOP_MENU_LINK_ID] = { id: SHOP_MENU_LINK_ID, kind: "link", fields: { label: state.settings.name, url: state.settings.base } };
    if (state.enabled !== false)
        next.collections.menu = { memberIds: [...(next.collections.menu?.memberIds ?? []), SHOP_MENU_LINK_ID] };
    return next;
}
